import React, { useEffect, useMemo, useState } from 'react';
import { Skeleton, Slider } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateModalSideFilters,
  updateSiteFilter,
} from '../../../../modules/SearchDatePicker/searchDatePickerSlice';
import './sideFilterTime.less';
import { HoursFilter } from '../../interface';
import { RootState } from '../../../../store/store';
import { useLocation } from 'react-router-dom';
import useGetAttributeTranslation from '../../hooks/useGetAttributeTranslation';

const timeRangeToMinutesArray = (timeRangeString: string | null) => {
  if (!timeRangeString) return null;
  const [startString, endString] = timeRangeString.split('-');

  const [startHours, startMinutes] = startString.split(':').map(Number);
  const [endHours, endMinutes] = endString.split(':').map(Number);

  const startMinutesSinceMidnight = startHours * 60 + startMinutes;
  const endMinutesSinceMidnight = endHours * 60 + endMinutes;

  return [startMinutesSinceMidnight, endMinutesSinceMidnight];
};

interface Props {
  filter: HoursFilter;
  withModalScopeParams?: boolean;
  isLoading?: boolean;
  additionalPrice?: number;
  localSideFilters?: Record<string, any> | null;
  setLocalSideFilters?: (f: Record<string, any>) => void;
}

const SideFilterTime = ({
  filter,
  withModalScopeParams,
  isLoading,
  additionalPrice = 0,
  localSideFilters,
  setLocalSideFilters
}: Props) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const { sideFilters: reduxSideFilters, modalSideFilters } = useSelector(
    (state: RootState) => {
      return state.search;
    }
  );
  const sideFilters = useMemo(
    () => (localSideFilters ? localSideFilters : withModalScopeParams ? modalSideFilters : reduxSideFilters),
    [withModalScopeParams, modalSideFilters, reduxSideFilters, localSideFilters]
  );

  // MINUTES
  const start = 0;
  const end = 1439;
  const initialValue = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return (
      timeRangeToMinutesArray(searchParams.get(filter.attr)) ?? [start, end]
    );
  }, [filter.attr, location]);

  const [timeRange, setTimeRange] = useState<any>(initialValue);

  // Sync slider from Redux (populated from URL by updateSideFiltersFunction) or fall back to URL-based initialValue
  useEffect(() => {
    const reduxValue = sideFilters[filter.attr];
    if (reduxValue && reduxValue[0]) {
      const minutesValue = timeRangeToMinutesArray(reduxValue[0]);
      if (minutesValue) {
        setTimeRange(minutesValue);
        return;
      }
    }
    setTimeRange(initialValue);
  }, [sideFilters, filter, initialValue]);

  const handleTimeRangeChange = (newTimeRange: [number, number]) => {
    setTimeRange(newTimeRange);
  };

  const formatTime = (time: number): string => {
    const hours = Math.floor(time / 60);
    const minutes = time % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
      2,
      '0'
    )}`;
  };

  const commitFilter = (key: string, value: any) => {
    // pass value in the format of HH:MM-HH:MM
    const newValue = [`${formatTime(value[0])}-${formatTime(value[1])}`];

    if (setLocalSideFilters) {
      // update local copy immutably
      setLocalSideFilters({
        ...(localSideFilters || {}),
        [key]: newValue,
      });
      return;
    }
    const fn = withModalScopeParams ? updateModalSideFilters : updateSiteFilter;
    dispatch(fn({ key: filter.attr, value: newValue }));
  };

  const handleAfterChange = (newValue: any) => {
        commitFilter(filter.attr, newValue);

  };

  const attributeTranslation = useGetAttributeTranslation();

  if (isLoading) {
    return <Skeleton active paragraph={{ rows: 1 }} />;
  }

  return (
    <div className="side-filter-hours">
      <h2>{attributeTranslation(filter.attr)}</h2>

      <div>
        <span>{formatTime(timeRange[0])}</span> :{' '}
        <span>{formatTime(timeRange[1])}</span>
        <Slider
          range
          min={0}
          max={1439}
          step={30}
          onChange={handleTimeRangeChange}
          value={timeRange}
          tooltip={{ formatter: null }}
          onAfterChange={handleAfterChange}
        />
      </div>
    </div>
  );
};

export default SideFilterTime;
